import { Link, NavLink } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ChevronDown } from "lucide-react";
import api, { thumbUrl } from "@/lib/api";
import { ServiceIcon } from "@/lib/serviceIcons";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

export const useServices = () => useQuery({ queryKey: ["services"], queryFn: async () => (await api.get("/services")).data, staleTime: 60000 });
const serviceTo = (s) => s.link || `/services/${s.slug}`;

export const ServicesNav = () => {
  const { data: services = [] } = useServices();
  if (!services.length) return null;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button data-testid="nav-services" className="px-3 py-2 text-sm font-medium rounded-lg transition-colors text-zinc-400 hover:text-white hover:bg-zinc-900 inline-flex items-center gap-1 data-[state=open]:text-white data-[state=open]:bg-zinc-800/70">
          Services <ChevronDown size={14} />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="bg-[#1e222d] border-zinc-800 text-zinc-200 min-w-[240px]" data-testid="nav-services-menu">
        {services.map((s) => (
          <DropdownMenuItem key={s.service_id} asChild>
            <Link to={serviceTo(s)} data-testid={`nav-service-${s.slug}`} className="flex items-center gap-3 cursor-pointer">
              {s.image_url ? <img src={thumbUrl(s.image_url, 64)} alt="" className="w-7 h-7 rounded-md object-cover" /> : <span className="w-7 h-7 rounded-md bg-[#ff6a13]/15 text-[#ff6a13] inline-flex items-center justify-center"><ServiceIcon name={s.icon} size={15} /></span>}
              <span className="flex-1 min-w-0"><span className="block truncate font-medium">{s.name}</span>{s.price_from && <span className="block text-[11px] text-zinc-500">From {s.price_from}</span>}</span>
            </Link>
          </DropdownMenuItem>
        ))}
        <DropdownMenuSeparator className="bg-zinc-800" />
        <DropdownMenuItem asChild><Link to="/" data-testid="nav-services-all" className="cursor-pointer text-[#22c3d6]">All services</Link></DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export const ServicesMobileNav = () => {
  const { data: services = [] } = useServices();
  if (!services.length) return null;
  return (
    <div className="mt-2" data-testid="mobile-services">
      <p className="px-4 pt-2 pb-1 text-[11px] font-mono-p uppercase tracking-widest text-zinc-500">Services</p>
      <div className="grid grid-cols-2 gap-1">
        {services.map((s) => (
          <NavLink key={s.service_id} to={serviceTo(s)} data-testid={`mobile-nav-service-${s.slug}`} className="flex items-center gap-2 px-3 py-3 text-sm font-semibold rounded-xl border border-zinc-800/80 text-zinc-200 hover:bg-zinc-900">
            {s.image_url ? <img src={thumbUrl(s.image_url, 64)} alt="" className="w-6 h-6 rounded object-cover" /> : <ServiceIcon name={s.icon} size={16} className="text-[#ff6a13] shrink-0" />}
            <span className="truncate">{s.name}</span>
          </NavLink>
        ))}
      </div>
    </div>
  );
};
